/**
 * Vulnerability export configuration
 *
 * COMPLEXITY: Medium
 * - Status filter: Multiple options with "STARTS WITH" operator
 * - Severity filter: Priority-based with RiskTag styling
 * - 26 columns available
 * - Simple query (no relationships, direct node filters)
 * - Mode-aware filtering: Legacy mode excludes Triaged vulnerabilities
 *
 * MIGRATION FROM: src/sections/export/ExportVulnerabilities.tsx
 */

import type { Severity } from '@praetorian-chariot/ui';

import type { UniversalExportConfig } from '@/components/export/universal/types';
import { GraphQueryBuilder } from '@/components/export/universal/utils';
import type { Risk } from '@/types';
import {
  RiskPriority,
  RiskSeverity,
  RiskStatus,
  RiskStatusLabel,
} from '@/types';
import type { VulnerabilityStatusMode } from '@/utils/vulnerabilityStatus.util';

/**
 * Status filter options
 * Maps display labels to backend status codes
 */
const STATUS_OPTIONS = [
  { label: RiskStatusLabel[RiskStatus.Triaged], value: RiskStatus.Triaged },
  { label: RiskStatusLabel[RiskStatus.Opened], value: RiskStatus.Opened },
  {
    label: RiskStatusLabel[RiskStatus.Remediated],
    value: RiskStatus.Remediated,
  },
  { label: RiskStatusLabel[RiskStatus.Accepted], value: RiskStatus.Accepted },
] as const;

/**
 * Severity filter options
 * Maps display labels to priority scores with severity tags
 */
const SEVERITY_OPTIONS = [
  {
    label: 'Critical',
    value: RiskPriority[RiskSeverity.Critical],
    severity: 'critical' as Severity,
  },
  {
    label: 'High',
    value: RiskPriority[RiskSeverity.High],
    severity: 'high' as Severity,
  },
  {
    label: 'Medium',
    value: RiskPriority[RiskSeverity.Medium],
    severity: 'medium' as Severity,
  },
  {
    label: 'Low',
    value: RiskPriority[RiskSeverity.Low],
    severity: 'low' as Severity,
  },
  {
    label: 'Info',
    value: RiskPriority[RiskSeverity.Info],
    severity: 'info' as Severity,
  },
  {
    label: 'Exposure',
    value: RiskPriority[RiskSeverity.Exposure],
    severity: 'exposure' as Severity,
  },
] as const;

/**
 * Column definitions - 28 columns total
 * Based on ExportVulnerabilities.tsx lines 21-60
 * Added: tags, updated (valid Risk model fields)
 */
const VULNERABILITY_COLUMNS = [
  { id: 'title', label: 'Title', defaultSelected: true },
  { id: 'severity', label: 'Severity', defaultSelected: true },
  { id: 'status', label: 'Status', defaultSelected: true },
  { id: 'tags', label: 'Tags', defaultSelected: true },
  { id: 'updated', label: 'Updated', defaultSelected: true },
  { id: 'description', label: 'Description', defaultSelected: true },
  { id: 'impact', label: 'Impact', defaultSelected: true },
  { id: 'recommendation', label: 'Recommendation', defaultSelected: true },
  { id: 'references', label: 'References', defaultSelected: true },
  { id: 'evidence', label: 'Evidence', defaultSelected: true },
  {
    id: 'auxiliary_evidence',
    label: 'Auxiliary Evidence',
    defaultSelected: true,
  },
  { id: 'first_seen', label: 'First Seen', defaultSelected: true },
  { id: 'last_seen', label: 'Last Seen', defaultSelected: true },
  { id: 'asset_name', label: 'Asset Name', defaultSelected: true },
  { id: 'asset_identifier', label: 'Asset Identifier', defaultSelected: true },
  { id: 'assets_impacted', label: 'Assets Impacted', defaultSelected: true },
  { id: 'cve', label: 'CVE', defaultSelected: true },
  { id: 'cisa_kev', label: 'CISA KEV', defaultSelected: true },
  { id: 'cvss_score', label: 'CVSS Score', defaultSelected: true },
  { id: 'epss_score', label: 'EPSS Score', defaultSelected: true },
  { id: 'exploits', label: 'Exploits', defaultSelected: true },
  { id: 'mitre_attack', label: 'MITRE ATT&CK', defaultSelected: true },
  { id: 'mitre_cwe', label: 'MITRE CWE', defaultSelected: true },
  { id: 'public_exploit', label: 'Public Exploit', defaultSelected: true },
  { id: 'threat_actors', label: 'Threat Actors', defaultSelected: true },
  { id: 'notes', label: 'Notes', defaultSelected: true },
  { id: 'history', label: 'History', defaultSelected: true },
] as const;

/**
 * Vulnerability export configuration
 */
export const vulnerabilityExportConfig: UniversalExportConfig<Risk> = {
  entityType: 'Vulnerability',
  apiEntityType: 'risk',
  storageKey: 'ADVANCE_EXPORT_OPTIONS.vulnerabilities.v2',

  filterSections: [
    {
      id: 'status',
      label: 'Status',
      options: [...STATUS_OPTIONS],
      defaultSelected: [], // No default status filter (export all statuses)
      showWhenSelected: false,
    },
    {
      id: 'severity',
      label: 'Severity',
      options: [...SEVERITY_OPTIONS],
      defaultSelected: [], // No default severity filter
      showWhenSelected: false,
      styleType: 'table', // Use table style for RiskTag display
    },
  ],

  columns: [...VULNERABILITY_COLUMNS],

  formats: [
    { label: 'CSV', value: 'csv' },
    { label: 'JSON', value: 'json' },
  ],
  defaultFormat: { label: 'CSV', value: 'csv' },

  /**
   * Build GraphQL query for vulnerability export
   *
   * QUERY STRUCTURE (from ExportVulnerabilities.tsx lines 184-220):
   * - Node: Risk
   * - Status filter: STARTS WITH operator
   * - Severity filter: Exact match on priority
   * - No relationships needed
   */
  buildQuery: (
    filters: Readonly<Record<string, readonly Readonly<any>[]>>,
    _selectedRows: readonly string[]
  ) => {
    let builder = GraphQueryBuilder.create(['Risk']);

    // Add status filter with STARTS WITH operator
    if (filters.status && filters.status.length > 0) {
      const statusValues = filters.status.map((s: any) => s.value as string);
      builder = builder.filter('status', 'STARTS WITH', statusValues);
    }

    // Add severity filter (exact match on priority)
    if (filters.severity && filters.severity.length > 0) {
      const priorityValues = filters.severity.map(
        (s: any) => s.value as number
      );
      builder = builder.filter('priority', '=', priorityValues);
    }

    return builder.orderBy('name', false).build();
  },

  /**
   * Validate export selections
   *
   * RULES:
   * - At least one column must be selected
   * - Max 100,000 rows (handled by default validation in UniversalExportModal)
   */
  validateExport: (
    _filters: Readonly<Record<string, readonly Readonly<any>[]>>,
    columns: readonly Readonly<any>[],
    _selectedRows: readonly string[]
  ) => {
    if (columns.length === 0) {
      return [
        {
          field: 'columns' as const,
          message: 'Please select at least one column to export.',
        },
      ];
    }
    return null;
  },
};

/**
 * Creates a mode-aware vulnerability export configuration.
 *
 * In legacy mode:
 * - Triaged status is excluded from status filter options
 * - Query excludes Triaged vulnerabilities when no status filter is applied
 *
 * In new mode:
 * - All status options are available
 * - No automatic filtering
 *
 * @param mode - The current vulnerability status mode ('legacy' | 'new')
 * @returns Mode-aware export configuration
 *
 * @example
 * ```tsx
 * const { mode } = useVulnerabilityStatusMode();
 * const exportConfig = createVulnerabilityExportConfig(mode);
 *
 * <UniversalExportModal config={exportConfig} ... />
 * ```
 */
export function createVulnerabilityExportConfig(
  mode: VulnerabilityStatusMode
): UniversalExportConfig<Risk> {
  // In legacy mode, exclude Triaged from status options
  const statusOptions =
    mode === 'legacy'
      ? STATUS_OPTIONS.filter(opt => opt.value !== RiskStatus.Triaged)
      : STATUS_OPTIONS;

  // Non-Triaged statuses for query filtering in legacy mode
  const nonTriagedStatuses = [
    RiskStatus.Opened,
    RiskStatus.Remediated,
    RiskStatus.Accepted,
    RiskStatus.Deleted,
  ];

  return {
    ...vulnerabilityExportConfig,
    filterSections: [
      {
        id: 'status',
        label: 'Status',
        options: [...statusOptions],
        defaultSelected: [],
        showWhenSelected: false,
      },
      // Severity section remains unchanged
      vulnerabilityExportConfig.filterSections[1],
    ],
    buildQuery: (
      filters: Readonly<Record<string, readonly Readonly<any>[]>>,
      _selectedRows: readonly string[]
    ) => {
      let builder = GraphQueryBuilder.create(['Risk']);

      // In legacy mode with no status filter, exclude Triaged vulnerabilities
      if (mode === 'legacy') {
        if (!filters.status || filters.status.length === 0) {
          // Apply filter to exclude Triaged by only including non-Triaged statuses
          builder = builder.filter('status', 'STARTS WITH', nonTriagedStatuses);
        } else {
          // User selected specific statuses - filter out Triaged if somehow included
          const statusValues = filters.status
            .map((s: any) => s.value as string)
            .filter((v: string) => v !== RiskStatus.Triaged);

          if (statusValues.length > 0) {
            builder = builder.filter('status', 'STARTS WITH', statusValues);
          } else {
            // If all selections were Triaged (shouldn't happen), use non-Triaged
            builder = builder.filter(
              'status',
              'STARTS WITH',
              nonTriagedStatuses
            );
          }
        }
      } else {
        // New mode - use normal status filtering
        if (filters.status && filters.status.length > 0) {
          const statusValues = filters.status.map(
            (s: any) => s.value as string
          );
          builder = builder.filter('status', 'STARTS WITH', statusValues);
        }
      }

      // Add severity filter (unchanged)
      if (filters.severity && filters.severity.length > 0) {
        const priorityValues = filters.severity.map(
          (s: any) => s.value as number
        );
        builder = builder.filter('priority', '=', priorityValues);
      }

      return builder.orderBy('name', false).build();
    },
  };
}
